import { useCallback, useMemo, useState } from 'react';
import { useConsumptions } from '../../../hooks/useConsumptions';
import { useExternalOrders } from '../../../hooks/useExternalOrders';
import { useWarehouses } from '../../../hooks/useWarehouses';
import { useUsers } from '../../../hooks/useUsers';
import { ITEMS_PER_PAGE } from '../constants/consumptionsConstants';
import {
  buildOrdersByUuid,
  buildUsersByUuid,
  buildWarehousesByUuid,
  filterConsumptions,
  mapConsumptionToDisplay,
} from '../utils/consumptionsUtils';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';

export const useConsumptionsTable = () => {
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedConsumptionId, setSelectedConsumptionId] = useState(null);

  const { data: consumptions = [], isLoading, refetch } = useConsumptions();
  const { data: orders = [] } = useExternalOrders();
  const { data: warehouses = [] } = useWarehouses();
  const { data: users = [] } = useUsers();

  const ordersByUuid = useMemo(() => buildOrdersByUuid(orders), [orders]);
  const warehousesByUuid = useMemo(() => buildWarehousesByUuid(warehouses), [warehouses]);
  const usersByUuid = useMemo(() => buildUsersByUuid(users), [users]);

  const displayConsumptions = useMemo(
    () =>
      consumptions.map((consumption) =>
        mapConsumptionToDisplay(consumption, {
          ordersByUuid,
          warehousesByUuid,
          usersByUuid,
        })
      ),
    [consumptions, ordersByUuid, warehousesByUuid, usersByUuid]
  );

  const filterFn = useCallback(
    (collection, term) => filterConsumptions(collection, term),
    []
  );

  const {
    data,
    totalCount,
    totalPages,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: displayConsumptions,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn,
  });

  const handleViewDetail = (consumption) => {
    setSelectedConsumptionId(consumption.uuid);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedConsumptionId(null);
  };

  const handleRefreshList = async () => {
    await refetch();
  };

  return {
    data,
    isLoading,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    detailDialogOpen,
    selectedConsumptionId,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleViewDetail,
    handleCloseDetailDialog,
    handleRefreshList,
  };
};
